import React, { useEffect, useState } from 'react';
import { useSessionState } from '../hooks/useSessionState';
import RibbonActions from '../components/RibbonActions';
import { useNavigate } from 'react-router-dom';
import { getProyectos, createProyecto, updateProyecto, getClientes, updateEstadoProy } from '../services/api';
import FiltroPanel from '../components/FiltroPanel';

const ESTADOS = ['activo','en_progreso','pausado','completado','cancelado'];
const EC = { activo:'#10b981', en_progreso:'#3b82f6', pausado:'#f59e0b', completado:'#8b5cf6', cancelado:'#ef4444' };

// Ícono lápiz monocromático (misma línea visual que el resto de la app)
const IcLapiz = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

export default function Proyectos() {
  const navigate = useNavigate();
  const [lista, setLista] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [filtro, setFiltro] = useSessionState('proy_filtro', '');
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null); // null = crear, id = editar
  const [form, setForm] = useState({ nombre:'', cliente_id:'', fecha_inicio:'', fecha_fin_estimada:'', presupuesto:'', descripcion:'' });
  const [saving, setSaving] = useState(false);

  const load = () => getProyectos(filtro ? { estado:filtro } : {}).then(r => setLista(r.data));
  useEffect(() => { load(); getClientes().then(r => setClientes(r.data)); }, []);
  useEffect(() => { load(); }, [filtro]);

  const soloFecha = v => v ? String(v).slice(0,10) : '';
  const vacio = { nombre:'', cliente_id:'', fecha_inicio:'', fecha_fin_estimada:'', presupuesto:'', descripcion:'' };

  const abrirNuevo = () => { setEditId(null); setForm(vacio); setModal(true); };
  const abrirEditar = (p) => {
    setEditId(p.id);
    setForm({
      nombre: p.nombre || '',
      cliente_id: p.cliente_id || '',
      fecha_inicio: soloFecha(p.fecha_inicio),
      fecha_fin_estimada: soloFecha(p.fecha_fin_estimada),
      presupuesto: p.presupuesto ?? '',
      descripcion: p.descripcion || '',
    });
    setModal(true);
  };
  const cerrarModal = () => { setModal(false); setEditId(null); setForm(vacio); };

  const handleSave = async () => {
    if (!form.nombre || !form.cliente_id) return alert('Complete los campos requeridos');
    setSaving(true);
    try {
      if (editId) await updateProyecto(editId, form);
      else        await createProyecto(form);
      cerrarModal();
      load();
    } catch(e) { alert(e.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div style={s.pageHeader}>
        <div><h1 style={s.h1}>Proyectos</h1><p style={s.sub}>Seguimiento de proyectos de ingeniería</p></div>
        <RibbonActions><div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <FiltroPanel
            fields={[{ tipo: 'select', label: 'Estado', value: filtro, onChange: setFiltro, capitalize: true, options: [{ value: '', label: 'Todos' }, ...ESTADOS.map(e => ({ value: e, label: e }))] }]}
            onClear={() => setFiltro('')}
          />
          <button style={s.btnPrimary} onClick={abrirNuevo}>+ Nuevo proyecto</button>
        </div></RibbonActions>
      </div>

      <div style={s.grid}>
        {lista.length === 0 && <div style={s.empty}>Sin proyectos registrados</div>}
        {lista.map(p => (
          <div key={p.id} style={s.proyCard} onClick={() => navigate(`/proyectos/${p.id}`)}>
            <div style={s.proyTop}>
              <span style={s.proyCode}>{p.codigo}</span>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ ...s.badge, background:EC[p.estado]+'22', color:EC[p.estado] }}>{p.estado}</span>
                <button
                  style={s.editBtn}
                  title="Editar proyecto"
                  onClick={(e) => { e.stopPropagation(); abrirEditar(p); }}
                ><IcLapiz /> Editar</button>
              </div>
            </div>
            <div style={s.proyName}>{p.nombre}</div>
            <div style={s.proyCliente}>{p.cliente_nombre}</div>
            <div style={s.proyFooter}>
              <span style={s.proyMeta}>👤 {p.responsable_nombre || 'Sin asignar'}</span>
              {p.presupuesto && <span style={s.proyMeta}>💰 ${Number(p.presupuesto).toLocaleString('es-CO')}</span>}
            </div>
            {p.fecha_fin_estimada && (
              <div style={s.proyDate}>📅 Fin estimado: {new Date(p.fecha_fin_estimada).toLocaleDateString('es-CO')}</div>
            )}
          </div>
        ))}
      </div>

      {modal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>{editId ? 'Editar proyecto' : 'Nuevo proyecto'}</h2>
              <button style={s.closeBtn} onClick={cerrarModal}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.field}>
                <label style={s.label}>Nombre del proyecto *</label>
                <input style={s.input} value={form.nombre} onChange={e => setForm({...form, nombre:e.target.value})} placeholder="Diseño estructural edificio..." />
              </div>
              <div style={s.row2}>
                <div style={s.field}>
                  <label style={s.label}>Cliente *</label>
                  <select style={s.input} value={form.cliente_id} onChange={e => setForm({...form, cliente_id:e.target.value})}>
                    <option value="">Seleccionar</option>
                    {clientes.map(c => <option key={c.id} value={c.id}>{c.razon_social}</option>)}
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Presupuesto</label>
                  <input style={s.input} type="number" value={form.presupuesto} onChange={e => setForm({...form, presupuesto:e.target.value})} placeholder="0" />
                </div>
              </div>
              <div style={s.row2}>
                <div style={s.field}>
                  <label style={s.label}>Fecha inicio</label>
                  <input style={s.input} type="date" value={form.fecha_inicio} onChange={e => setForm({...form, fecha_inicio:e.target.value})} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Fecha fin estimada</label>
                  <input style={s.input} type="date" value={form.fecha_fin_estimada} onChange={e => setForm({...form, fecha_fin_estimada:e.target.value})} />
                </div>
              </div>
              <div style={s.field}>
                <label style={s.label}>Descripción</label>
                <textarea style={{ ...s.input, minHeight:70, resize:'vertical' }} value={form.descripcion} onChange={e => setForm({...form, descripcion:e.target.value})} />
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={cerrarModal}>Cancelar</button>
              <button style={{ ...s.btnPrimary, opacity:saving?0.7:1 }} onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : (editId ? 'Guardar cambios' : 'Crear proyecto')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  pageHeader:  { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 },
  h1:          { color:'#f1f5f9', fontSize:24, fontWeight:700, margin:'0 0 4px' },
  sub:         { color:'#64748b', fontSize:14, margin:0 },
  btnPrimary:  { background:'#1d4ed8', color:'#fff', border:'none', borderRadius:8, padding:'10px 20px', fontSize:14, fontWeight:600, cursor:'pointer' },
  btnSecondary:{ background:'#334155', color:'#cbd5e1', border:'none', borderRadius:8, padding:'10px 20px', fontSize:14, cursor:'pointer' },
  filtros:     { display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' },
  chip:        { background:'#1e293b', border:'1px solid #334155', color:'#94a3b8', padding:'6px 14px', borderRadius:20, fontSize:12, cursor:'pointer' },
  chipActive:  { background:'#1d4ed8', borderColor:'#1d4ed8', color:'#fff' },
  grid:        { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 },
  empty:       { color:'#475569', fontSize:14, gridColumn:'1/-1', textAlign:'center', padding:40 },
  proyCard:    { background:'#1e293b', borderRadius:12, padding:20, cursor:'pointer', border:'1px solid #334155', transition:'border-color .15s' },
  proyTop:     { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 },
  proyCode:    { color:'#3b82f6', fontSize:12, fontWeight:600 },
  badge:       { padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600 },
  editBtn:     { display:'inline-flex', alignItems:'center', gap:5, background:'transparent', color:'#94a3b8', border:'1px solid #334155', borderRadius:6, padding:'3px 9px', fontSize:11, cursor:'pointer', whiteSpace:'nowrap' },
  proyName:    { color:'#f1f5f9', fontSize:15, fontWeight:600, marginBottom:4 },
  proyCliente: { color:'#64748b', fontSize:13, marginBottom:12 },
  proyFooter:  { display:'flex', justifyContent:'space-between', marginBottom:6 },
  proyMeta:    { color:'#94a3b8', fontSize:12 },
  proyDate:    { color:'#64748b', fontSize:12, marginTop:8 },
  overlay:     { position:'fixed', inset:0, background:'rgba(0,0,0,.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100 },
  modal:       { background:'#1e293b', borderRadius:16, width:'90%', maxWidth:600, maxHeight:'90vh', display:'flex', flexDirection:'column', border:'1px solid #334155' },
  modalHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'13px 20px', borderBottom:'1px solid #334155' },
  modalTitle:  { color:'#f1f5f9', fontSize:16, fontWeight:700, margin:0 },
  closeBtn:    { background:'transparent', border:'none', color:'#64748b', fontSize:18, cursor:'pointer' },
  modalBody:   { padding:'14px 20px', overflowY:'auto', flex:1 },
  modalFooter: { display:'flex', justifyContent:'flex-end', gap:10, padding:'12px 20px', borderTop:'1px solid #334155' },
  row2:        { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 },
  field:       { marginBottom:10 },
  label:       { display:'block', color:'#94a3b8', fontSize:12, marginBottom:4, fontWeight:500 },
  input:       { width:'100%', height:34, padding:'0 12px', background:'#0f172a', border:'1px solid #334155', borderRadius:8, color:'#f1f5f9', fontSize:13, boxSizing:'border-box', outline:'none' },
};
